/**
 * I-1470 EM-9 (`docs/concept-failure-messages.md`): the request id the server gave a failed REST
 * call, which is the reference a screen shows for it. `RequestErrorFilter` writes it into every
 * failure's body (`requestId`) and its `x-request-id` header, and logs the method, the route and the
 * cause under it, so whoever reads the server's log finds the call from the id a person copied.
 *
 * ⛔ Never the call's method or route: "GET /work-areas/:id/views (500)" was this file's reference
 * until I-1470, and a route is code words to the person reading the failure, as an operation name
 * is.
 */

import { isGenericServerText } from './failureReasons';

/** The shape `RequestErrorFilter` mints and accepts: a uuid, or a caller's id of the same alphabet. */
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;

interface IFailedCall {
  status?: unknown;
  /** The desktop app's cloud requester keeps the answer's body here (`DesktopCloudTransportError`). */
  body?: unknown;
  response?: { status?: unknown; data?: unknown; headers?: Record<string, unknown> };
}

/** The status a failed request answered with, or null when no answer came. */
function statusOf(error: IFailedCall): number | null {
  const status = error.response?.status ?? error.status;
  return typeof status === 'number' ? status : null;
}

/** The answer's body, from Axios or from the desktop app's cloud requester. */
function bodyOf(error: IFailedCall): { message?: unknown; requestId?: unknown } | null {
  const body = error.response ? error.response.data : error.body;
  return body && typeof body === 'object' ? (body as { message?: unknown }) : null;
}

/**
 * A refusal the server explained in its own words — a 4xx whose body says why, such as "That is not
 * the password for this account." That sentence is the answer the person acts on, not a failure to
 * place, so it carries no reference (EM-7).
 */
function answeredWithItsOwnReason(error: IFailedCall): boolean {
  const status = statusOf(error);
  if (status === null || status < 400 || status >= 500) return false;
  const message = bodyOf(error)?.message;
  const sentences = Array.isArray(message) ? message : [message];
  return sentences.some(
    (entry) => typeof entry === 'string' && entry.trim() !== '' && !isGenericServerText(entry),
  );
}

/**
 * The request id a failed REST call's answer carries, or null: no answer, none given, explained, or
 * a refusal the server did not log. `RequestErrorFilter` logs a 5xx under its id, and a refusal it
 * answers without a line in the log; an id that finds nothing there is no reference (I-1470, EM-12).
 */
export function readRequestId(error: unknown): string | null {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) return null;
  const call = error as IFailedCall;
  const status = statusOf(call);
  if (status === null || status < 500 || answeredWithItsOwnReason(call)) return null;
  const id = bodyOf(call)?.requestId ?? call.response?.headers?.['x-request-id'];
  return typeof id === 'string' && REQUEST_ID_PATTERN.test(id) ? id : null;
}
